import React, { useEffect, useState } from 'react';
import './index.css';
import axios from 'axios';

const Dashboard = () => {
  const api = process.env.REACT_APP_API;
  const [enquiries, setEnquiries] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [paidAmounts, setPaidAmounts] = useState({});
  const [paidLoading, setPaidLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterValue, setFilterValue] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [counselorFees, setCounselorFees] = useState([]);
  const [projectJoinedCount, setProjectJoinedCount] = useState(0);

  const counselor = localStorage.getItem('name') || '';
  const center = localStorage.getItem('center') || '';

  // Fetch enquiries
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      setError("Unauthorized: No token");
      setLoading(false);
      return;
    }
    fetch(`${api}/enquiry-status`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => {
        const filtered = data.filter(item => item.centerName === center);
        setEnquiries(filtered);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError("Error fetching enquiries");
        setLoading(false);
      });
  }, [api, center]);

  // Fetch admissions
  useEffect(() => {
    const fetchAdmissions = async () => {
      try {
        const res = await axios.get(`${api}/api/admissions`);
        setAdmissions(res.data);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error fetching admissions");
        setLoading(false);
      }
    };
    fetchAdmissions();
  }, [api]);

  // Fetch paid amounts for each student
  useEffect(() => {
    const fetchPaidAmounts = async () => {
      setPaidLoading(true);
      const counselorAdmissions = admissions.filter(
        a => a.counselorName?.toLowerCase() === counselor.toLowerCase()
      );
      const result = {};
      await Promise.all(
        counselorAdmissions.map(async (adm) => {
          try {
            const res = await axios.get(`${api}/fees/total-paid/${adm.IdNo}`);
            result[adm.IdNo] = res.data.totalPaid || 0;
          } catch (err) {
            console.error(`Error fetching paid amount for IdNo: ${adm.IdNo}`, err);
            result[adm.IdNo] = 0;
          }
        })
      );
      setPaidAmounts(result);
      setPaidLoading(false);
    };
    if (admissions.length > 0) fetchPaidAmounts();
  }, [admissions, api, counselor]);

  // Compute counselor's admissions count locally from already-fetched admissions
  useEffect(() => {
    const count = admissions.filter(
      a => a.counselorName?.toLowerCase() === counselor.toLowerCase()
    ).length;
    setProjectJoinedCount(count);
  }, [admissions, counselor]);

  // Date filter logic
  const filterByDate = (dateStr) => {
    if (!dateStr || filterType === 'all' || !filterValue) return true;
    const date = new Date(dateStr);
    if (filterType === 'day') return date.toISOString().slice(0, 10) === filterValue;
    if (filterType === 'month') return date.toISOString().slice(0, 7) === filterValue;
    if (filterType === 'year') return date.getFullYear().toString() === filterValue;
    return true;
  };

  // Date range filter logic
  const isWithinRange = (dateStr) => {
    if (!fromDate && !toDate) return true;
    const date = new Date(dateStr);
    if (fromDate && date < new Date(fromDate)) return false;
    if (toDate && date > new Date(toDate)) return false;
    return true;
  };

  const filteredEnquiries = enquiries.filter(
    e => e.counselorName?.toLowerCase() === counselor.toLowerCase() && filterByDate(e.createdAt)
  );

  const joinedCount = filteredEnquiries.filter(
    e => e.status?.toLowerCase() === 'joined'
  ).length;
  const notJoinedCount = filteredEnquiries.length - joinedCount;

  const counselorAdmissions = admissions.filter(
    a => a.counselorName?.toLowerCase() === counselor.toLowerCase()
  );

  const totalFees = counselorAdmissions.reduce(
    (sum, a) => sum + (parseFloat(a.totalFees) || 0), 0
  );

  const today = new Date();

  const filteredCounselorAdmissions = counselorAdmissions.filter(
    a => filterByDate(a.dateOfJoining)
  );

  const feesByDate = filteredCounselorAdmissions.reduce(
    (sum, a) => sum + (parseFloat(a.totalFees) || 0), 0
  );

  // Date range filtered admissions
  const rangeCounselorAdmissions = counselorAdmissions.filter(
    a => isWithinRange(a.createdAt)
  );

  const totalFeesInRange = rangeCounselorAdmissions.reduce(
    (sum, a) => sum + (parseFloat(a.totalFees) || 0), 0
  );
  const totalPaidInRange = rangeCounselorAdmissions.reduce((sum, adm) => {
    const paid = paidAmounts[adm.IdNo] ?? 0;
    return sum + paid;
  }, 0);
  const totalPendingInRange = totalFeesInRange - totalPaidInRange;

  const totalPaidByCounselor = counselorAdmissions.reduce((sum, adm) => {
    const paid = paidAmounts[adm.IdNo] ?? 0;
    return sum + paid;
  }, 0);

  useEffect(() => {
    const fetchCounselorFees = async () => {
      if (counselorAdmissions.length === 0) return;
      try {
        const idNos = counselorAdmissions.map(a => a.IdNo);
        const res = await axios.post(`${api}/fees/by-idnos`, { idNos });
        setCounselorFees(res.data);
      } catch (err) {
        setCounselorFees([]);
      }
    };
    fetchCounselorFees();
  }, [counselorAdmissions, api]);

  const handleFilterChange = (type, value) => {
    setFilterType(type);
    setFilterValue(value);
  };

  // Flatten all terms into a single array of { amountPaid, datePaid }
  const allPayments = counselorFees.flatMap(feesDoc =>
    (feesDoc.terms || []).map(term => ({
      amountPaid: parseFloat(term.amountPaid) || 0,
      datePaid: term.datePaid
    }))
  );

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Counselor Dashboard</h2>

      {/* Filters */}
      <div className="dashboard-filters">
        <label>Filter:</label>
        <select value={filterType} onChange={e => handleFilterChange(e.target.value, '')}>
          <option value="all">All</option>
          <option value="day">Day</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
        {filterType === 'day' && (
          <input type="date" value={filterValue} onChange={e => setFilterValue(e.target.value)} />
        )}
        {filterType === 'month' && (
          <input type="month" value={filterValue} onChange={e => setFilterValue(e.target.value)} />
        )}
        {filterType === 'year' && (
          <input
            type="number"
            min="2000"
            max={today.getFullYear()}
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            placeholder="Year"
          />
        )}
      </div>

      {/* Date Range */}
      <div className="dashboard-date-range">
        <label>From: </label>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <label>To: </label>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div className="dashboard-error">{error}</div>
      ) : (
        <>
          <div className="dashboard-stats">
            <div className="dashboard-card">Total Enquiries: {filteredEnquiries.length}</div>
            <div className="dashboard-card">Not Joined: {notJoinedCount}</div>
            <div className="dashboard-card">Admissions Count: {projectJoinedCount}</div>
            <div className="dashboard-card">Total Fees (All): ₹ {totalFees.toLocaleString()}</div>
            <div className="dashboard-card">Total Paid: ₹ {totalPaidByCounselor.toLocaleString()}</div>
            <div className="dashboard-card">Total Pending (Range): ₹ {totalPendingInRange.toLocaleString()}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
